import React, { useEffect, useState } from 'react';
import config from '../../config';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import {
    getHtmlRendererObjectUrl,
    getHtmlRendererObjectUrlFromParts,
    getHtmlRendererParts,
    getResultRendererIframeOrigin,
    getTransferableHtmlBuffers,
    isIframeSandboxSupported,
} from './utils';
import { getIframeMessageProcessor } from './iframeMessageProcessor';
import { IFileNode, IFileTreeNode } from '@apiHooks';
import LoadingBanner from '@components/atoms/LoadingBanner';
import { IFileInIframe, IFrameParentMessageType } from './iframe.types';

interface IBaseProps {
    allowedRemoteHosts?: string[];
    linkableRemoteHosts?: string[];
    allowOpenUrlForAnyRemoteHost?: boolean;
    job?: {
        uuid: string;
        auth_token: string;
        app_uri: string;
        created_at: string;
        can_access_all_results_of_user: boolean;
        can_access_all_index_tables_of_user: boolean;
        can_access_all_data_records_of_user: boolean;
        can_access_oauth_token_of_user: boolean;
    };
    user?: { email: string; handle: string };
    overrideFileData?: Record<string, Uint8Array>;
    presignedStorageUrl?: string;
    growHeightToFitContent?: boolean;
    viewRaw?: boolean;
    allowScrollNavigationOnHover?: boolean;
    enableDebouncedAutoResize?: boolean;
    centerImage?: boolean;
    loadingFallback?: React.ReactNode;
    openSameOriginLinksInSameTab?: boolean;
    // When set, the content is served from a dedicated host (bootstrap-<random string>...) instead of
    // a blob URL, so each rendered file or dashboard app gets its own browser process. Requires the
    // result renderer hostname to be configured; otherwise it falls back to the blob URL.
    renderInIsolatedOrigin?: boolean;
}

interface ISandBoxedIframeProps extends IBaseProps {
    fileTreeNode?: IFileTreeNode | IFileNode;
    onLoadCallback?: (utils: { callUtilFunction: (functionName: string, args: any[]) => Promise<any> }) => void;
}

interface IFileRendererProps extends IBaseProps {
    fileTreeNode: IFileTreeNode | IFileNode;
}

export default function FileRenderer(props: IFileRendererProps): JSX.Element {
    const { fileTreeNode } = props;
    const sizeInBytes = 'size_in_bytes' in fileTreeNode ? fileTreeNode.size_in_bytes : fileTreeNode.size;
    const fullPath = 'full_path' in fileTreeNode ? fileTreeNode.full_path : fileTreeNode.dir_path + fileTreeNode.name;

    if (sizeInBytes > 200_000_000) {
        return (
            <div className='bg-red-400/10 p-3 text-center'>
                <div className='inline-flex items-center font-normal text-red-700'>
                    <ExclamationCircleIcon className='h-5 w-5 mr-1' />
                    <span>File is too large to render</span>
                </div>
            </div>
        );
    }
    return (
        <SandBoxedIframe
            {...props}
            renderInIsolatedOrigin={props.renderInIsolatedOrigin ?? true}
            key={`${props.job?.uuid || ''}-${fullPath}`}
        />
    );
}

export function SandBoxedIframe(props: ISandBoxedIframeProps): JSX.Element {
    const {
        allowedRemoteHosts,
        linkableRemoteHosts,
        allowOpenUrlForAnyRemoteHost,
        allowScrollNavigationOnHover,
        enableDebouncedAutoResize,
        centerImage,
        fileTreeNode,
        growHeightToFitContent,
        renderInIsolatedOrigin,
        job,
        user,
        onLoadCallback,
        openSameOriginLinksInSameTab,
        overrideFileData,
        presignedStorageUrl,
        viewRaw,
    } = props;

    if (enableDebouncedAutoResize && growHeightToFitContent) {
        throw new Error('Cannot use both enableDebouncedAutoResize and growHeightToFitContent');
    }

    if (allowOpenUrlForAnyRemoteHost && job) {
        throw new Error('Cannot use allowOpenUrlForAnyRemoteHost when job is set');
    }

    const file: IFileInIframe | null = !fileTreeNode
        ? null
        : {
              path: 'full_path' in fileTreeNode ? fileTreeNode.full_path : fileTreeNode.dir_path + fileTreeNode.name,
              type: 'full_path' in fileTreeNode ? 'output' : 'input',
              viewRaw: !!viewRaw,
          };

    if (typeof location === 'undefined') {
        return null;
    }

    const [isSandboxSupported] = useState<boolean>(isIframeSandboxSupported);
    if (!isSandboxSupported) {
        return (
            <div className='bg-red-400/10 p-3 text-center'>
                <div className='inline-flex items-center font-normal text-red-700'>
                    <ExclamationCircleIcon className='h-5 w-5 mr-1' />
                    <span>
                        It seems your browser does not support sandboxed rendering. Please use the latest version of
                        Chrome, Firefox, Edge or Safari.
                    </span>
                </div>
            </div>
        );
    }

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [iframeId] = useState(crypto.randomUUID());
    const [htmlObjectUrl, setHtmlObjectUrl] = useState<null | string>(null);
    const [isolatedIframeOrigin] = useState<string | null>(() =>
        renderInIsolatedOrigin && config.resultRendererHostname
            ? getResultRendererIframeOrigin(config.resultRendererHostname)
            : null,
    );
    const [isDocumentHiddenOnInit, setIsDocumentHiddenOnInit] = useState(document.hidden);
    // The iframe has to be mounted for the bootstrap to load, but it holds nothing to show until the
    // HTML is handed over to it, so the loading fallback is kept until then
    const [isAwaitingContentHandover, setIsAwaitingContentHandover] = useState(false);

    useEffect(() => {
        let isCancelled = false;

        const onVisibilitychange = () => {
            if (isDocumentHiddenOnInit && !document.hidden) {
                setIsDocumentHiddenOnInit(false);
            }
        };
        document.addEventListener('visibilitychange', onVisibilitychange);

        const processMessages = getIframeMessageProcessor({
            allowedRemoteHosts: allowedRemoteHosts || [],
            linkableRemoteHosts: linkableRemoteHosts || [],
            allowOpenUrlForAnyRemoteHost,
            iframeId,
            job,
            openSameOriginLinksInSameTab,
            overrideFileData,
            presignedStorageUrl,
            viewRaw,
            onLoadCallback,
        });
        window.addEventListener('message', processMessages);

        let resizeTimeout200ms: NodeJS.Timeout | null = null;
        let resizeTimeout3s: NodeJS.Timeout | null = null;
        let handleParentResize: (() => void) | null = null;
        if (enableDebouncedAutoResize) {
            const resizeIframe = () => {
                const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
                if (iframe?.contentWindow) {
                    // Send message to iframe because we cannot access the document height of the iframe directly due to origin isolation
                    iframe.contentWindow.postMessage({ type: IFrameParentMessageType.triggerResize }, '*');
                }
            };

            handleParentResize = () => {
                if (resizeTimeout200ms) clearTimeout(resizeTimeout200ms);
                if (resizeTimeout3s) clearTimeout(resizeTimeout3s);

                resizeTimeout200ms = setTimeout(resizeIframe, 200);
                resizeTimeout3s = setTimeout(resizeIframe, 3000);
            };

            window.addEventListener('resize', handleParentResize);
        }

        let handleBootstrapReady: ((event: MessageEvent) => void) | null = null;
        let bootstrapTimeout: NodeJS.Timeout | null = null;
        let createdObjectUrl: string | null = null;

        if (isolatedIframeOrigin) {
            // The bootstrap page signals it is ready, then we hand it the HTML which it rewrites into
            // its document (running its scripts) on the dedicated host. The ready message and the
            // HTML fetch can complete in either order, so we post once both have happened. The parts
            // are only turned into transferable buffers once handed over, as transferring detaches
            // them and they are still needed for the blob URL fallback below.
            let htmlParts: Uint8Array[] | null = null;
            let isBootstrapReady = false;
            let hasRendered = false;
            const renderIfReady = () => {
                const parts = htmlParts;
                if (hasRendered || parts === null || !isBootstrapReady) {
                    return;
                }
                hasRendered = true;
                if (bootstrapTimeout) clearTimeout(bootstrapTimeout);
                setIsAwaitingContentHandover(false);
                const iframe = document.getElementById(iframeId) as HTMLIFrameElement | null;
                const htmlBuffers = getTransferableHtmlBuffers(parts);
                iframe?.contentWindow?.postMessage(
                    { type: 'biolib-result-renderer-render', htmlBuffers },
                    '*',
                    htmlBuffers,
                );
            };

            const onBootstrapReady = (event: MessageEvent) => {
                if (
                    event.data?.type !== 'biolib-result-renderer-bootstrap-ready' ||
                    event.data?.iframeId !== iframeId
                ) {
                    return;
                }
                isBootstrapReady = true;
                renderIfReady();
            };
            handleBootstrapReady = onBootstrapReady;
            window.addEventListener('message', onBootstrapReady);

            // If the bootstrap does not report itself ready, the dedicated host is unreachable or is
            // served without the sandbox CSP it requires, so render from a blob URL instead. Only the
            // separation between renders is lost by doing so. The iframe is not mounted while the tab
            // is in the background, and there is nothing to hand over until the HTML is fetched, so we
            // only start counting once both are in place.
            let bootstrapReadyWaitStartedAt: number | null = null;
            const checkBootstrapReady = () => {
                if (hasRendered || isCancelled) {
                    return;
                }
                const parts = htmlParts;
                if (parts === null || !document.getElementById(iframeId)) {
                    bootstrapReadyWaitStartedAt = null;
                } else if (bootstrapReadyWaitStartedAt === null) {
                    bootstrapReadyWaitStartedAt = Date.now();
                } else if (Date.now() - bootstrapReadyWaitStartedAt >= 5_000) {
                    hasRendered = true;
                    window.removeEventListener('message', onBootstrapReady);
                    createdObjectUrl = getHtmlRendererObjectUrlFromParts(parts);
                    setIsAwaitingContentHandover(false);
                    setHtmlObjectUrl(createdObjectUrl);
                    return;
                }
                bootstrapTimeout = setTimeout(checkBootstrapReady, 1_000);
            };
            bootstrapTimeout = setTimeout(checkBootstrapReady, 1_000);

            getHtmlRendererParts({
                iframeId,
                file,
                job,
                user,
                presignedStorageUrl,
                growHeightToFitContent,
                enableDebouncedAutoResize,
                centerImage,
            })
                .then((parts) => {
                    if (isCancelled) {
                        return;
                    }
                    htmlParts = parts;
                    renderIfReady();
                })
                .catch((error) => {
                    hasRendered = true;
                    if (bootstrapTimeout) clearTimeout(bootstrapTimeout);
                    if (!isCancelled) {
                        setErrorMessage(`Failed to load. Please try refreshing the page. Got error: ${error}`);
                    }
                });

            // The bootstrap echoes the iframe ID back in its ready message so renderers on the same
            // page can be told apart. The random part changes on every render so the iframe reloads
            // the bootstrap (e.g. when the raw/rich view toggles) and re-emits its ready message.
            setIsAwaitingContentHandover(true);
            setHtmlObjectUrl(`${isolatedIframeOrigin}/?iframeId=${iframeId}&r=${crypto.randomUUID()}`);
        } else {
            getHtmlRendererObjectUrl({
                iframeId,
                file,
                job,
                user,
                presignedStorageUrl,
                growHeightToFitContent,
                enableDebouncedAutoResize,
                centerImage,
            })
                .then((objectUrl) => {
                    if (isCancelled) {
                        URL.revokeObjectURL(objectUrl);
                        return;
                    }
                    createdObjectUrl = objectUrl;
                    setHtmlObjectUrl(objectUrl);
                })
                .catch((error) => {
                    if (!isCancelled) {
                        setErrorMessage(`Failed to load. Please try refreshing the page. Got error: ${error}`);
                    }
                });
        }

        return () => {
            isCancelled = true;
            if (createdObjectUrl) {
                URL.revokeObjectURL(createdObjectUrl);
            }
            if (handleBootstrapReady) window.removeEventListener('message', handleBootstrapReady);
            window.removeEventListener('message', processMessages);
            document.removeEventListener('visibilitychange', onVisibilitychange);
            if (handleParentResize) window.removeEventListener('resize', handleParentResize);
            if (resizeTimeout200ms) clearTimeout(resizeTimeout200ms);
            if (resizeTimeout3s) clearTimeout(resizeTimeout3s);
            if (bootstrapTimeout) clearTimeout(bootstrapTimeout);
        };
    }, [viewRaw]);

    if (errorMessage) {
        return (
            <div className='bg-red-400/10 p-3 text-center'>
                <div className='inline-flex items-center font-normal text-red-700'>
                    <ExclamationCircleIcon className='h-5 w-5 mr-1' />
                    <span>{errorMessage}</span>
                </div>
            </div>
        );
    }

    if (!htmlObjectUrl || isDocumentHiddenOnInit) {
        return <>{props.loadingFallback ?? <LoadingBanner message='Loading...' />}</>;
    }

    // Blob renders share our origin, so the CSP is enforced via the iframe attribute. Dedicated-host
    // renders get their CSP from the proxy response header instead (the attribute would need the
    // cross-origin response to opt in with Allow-CSP-From, and would otherwise block the frame).
    const requiredCsp = htmlObjectUrl.startsWith('blob:')
        ? "default-src 'unsafe-inline' 'unsafe-eval' data: blob:;"
        : undefined;

    return (
        <>
            {isAwaitingContentHandover && (
                <div className='relative h-0'>
                    <div className='absolute inset-x-0 top-0 z-10'>
                        {props.loadingFallback ?? <LoadingBanner message='Loading...' />}
                    </div>
                </div>
            )}
            <iframe
                allow={config.isEnterprise ? 'clipboard-write; fullscreen' : undefined}
                // Chrome blocks a cross-origin frame whose response does not accept the required policy,
                // so it is only required of blob URLs. The render host sends the policy as a header itself.
                // @ts-ignore
                csp={requiredCsp}
                id={iframeId}
                name={iframeId}
                onMouseEnter={() => {
                    if (!allowScrollNavigationOnHover) {
                        document.documentElement.style.overscrollBehaviorX = 'none';
                    }
                }}
                onMouseLeave={() => {
                    if (!allowScrollNavigationOnHover) {
                        document.documentElement.style.overscrollBehaviorX = '';
                    }
                }}
                sandbox='allow-scripts allow-downloads'
                src={htmlObjectUrl}
                className={enableDebouncedAutoResize || growHeightToFitContent ? undefined : 'flex-1'}
                style={{
                    width: '100%',
                    padding: 0,
                    margin: 0,
                    height: enableDebouncedAutoResize ? 0 : growHeightToFitContent ? '600px' : '100%',
                    // Only hidden, not unmounted or taken out of the layout, as the bootstrap loads while
                    // mounted and the content is handed over at the size it is rendered at, so content
                    // measuring itself as it loads does not measure a size it is never shown at
                    ...(isAwaitingContentHandover ? { visibility: 'hidden' as const } : {}),
                }}
            />
        </>
    );
}
